// Comprehensive module content for OSHA Training Course

export const moduleContent = {
  1: {
    id: 1,
    title: "Introduction to OSHA & Workplace Safety",
    subtitle: "Understanding the Foundation of Workplace Safety",
    duration: "5-6 hours",
    description: "Learn the fundamentals of OSHA regulations, workers' rights, employer responsibilities, and why workplace safety matters for everyone.",
    heroImage: "https://images.unsplash.com/photo-1504307651254-35680f356dfd?w=1200",
    objectives: [
      "Understand OSHA's mission and organizational structure",
      "Identify key worker rights under OSHA regulations",
      "Recognize employer responsibilities for workplace safety",
      "Navigate the basic structure of OSHA standards (29 CFR)",
      "Appreciate the importance of building a safety culture"
    ],
    lessons: [
      {
        id: 1,
        title: "What is OSHA and Its Mission",
        duration: "45 min",
        content: `
          <h3>The Birth of Workplace Safety Regulation</h3>
          <p>The Occupational Safety and Health Administration (OSHA) was created by the <strong>Occupational Safety and Health Act of 1970</strong>, signed into law by President Richard Nixon on December 29, 1970. This landmark legislation came after decades of workplace tragedies and a growing recognition that workers needed federal protection.</p>
          
          <div class="highlight-box">
            <h4>OSHA's Mission Statement</h4>
            <p>"To ensure safe and healthful working conditions for workers by setting and enforcing standards and by providing training, outreach, education, and assistance."</p>
          </div>

          <h3>Key Statistics That Led to OSHA's Creation</h3>
          <p>Before OSHA, approximately <strong>14,000 workers were killed on the job each year</strong>. Millions more suffered injuries and illnesses. There was no comprehensive federal oversight of workplace safety conditions.</p>

          <h3>OSHA Today</h3>
          <p>OSHA is part of the U.S. Department of Labor and covers most private sector employers and workers in all 50 states, the District of Columbia, and other U.S. jurisdictions.</p>
          
          <ul>
            <li><strong>Federal OSHA</strong> - Covers 29 states and territories</li>
            <li><strong>State Plans</strong> - 22 states have their own OSHA-approved programs</li>
            <li><strong>Coverage</strong> - Over 130 million workers at 8+ million worksites</li>
          </ul>
        `,
        keyPoints: [
          "OSHA was established in 1970 under the OSH Act",
          "Mission: Ensure safe and healthful working conditions",
          "Covers most private sector employers in the US",
          "22 states have their own OSHA-approved state plans"
        ]
      },
      {
        id: 2,
        title: "Understanding Workers' Rights",
        duration: "50 min",
        content: `
          <h3>Your Rights Under OSHA</h3>
          <p>Every worker has fundamental rights when it comes to workplace safety. Understanding these rights empowers you to protect yourself and your coworkers.</p>

          <div class="highlight-box important">
            <h4>The Right to a Safe Workplace</h4>
            <p>You have the right to working conditions that do not pose a risk of serious harm. This is guaranteed under Section 5(a)(1) of the OSH Act, known as the "General Duty Clause."</p>
          </div>

          <h3>Core Worker Rights</h3>
          <ul>
            <li><strong>Right to Know</strong> - Access information about hazards, safety standards, and injury records</li>
            <li><strong>Right to Training</strong> - Receive training on workplace hazards in a language you understand</li>
            <li><strong>Right to Request Inspection</strong> - Ask OSHA to inspect your workplace if you believe hazards exist</li>
            <li><strong>Right to Report</strong> - Report injuries, illnesses, and hazards without fear of retaliation</li>
            <li><strong>Right to Participate</strong> - Take part in OSHA inspections and speak privately with inspectors</li>
          </ul>

          <h3>Protection Against Retaliation</h3>
          <p>Section 11(c) of the OSH Act protects workers from retaliation for exercising their safety rights. If you report a hazard, file a complaint, or participate in an OSHA inspection, your employer cannot:</p>
          <ul>
            <li>Fire or lay you off</li>
            <li>Demote you or cut your pay</li>
            <li>Reduce your hours</li>
            <li>Reassign you to an undesirable position</li>
            <li>Threaten or harass you</li>
          </ul>
        `,
        keyPoints: [
          "Workers have the right to a safe workplace free from known hazards",
          "You can request an OSHA inspection anonymously",
          "Training must be provided in a language workers understand",
          "Retaliation for reporting safety concerns is illegal"
        ]
      },
      {
        id: 3,
        title: "Employer Responsibilities Under OSHA",
        duration: "55 min",
        content: `
          <h3>The General Duty Clause</h3>
          <p>At the heart of employer responsibility is Section 5(a)(1), the General Duty Clause:</p>
          
          <div class="highlight-box">
            <p><em>"Each employer shall furnish to each of his employees employment and a place of employment which are free from recognized hazards that are causing or are likely to cause death or serious physical harm."</em></p>
          </div>

          <h3>Key Employer Obligations</h3>
          <ol>
            <li>
              <h4>Provide a Safe Workplace</h4>
              <p>Examine workplace conditions, eliminate or control hazards, and ensure workers have safe tools and equipment.</p>
            </li>
            <li>
              <h4>Comply with OSHA Standards</h4>
              <p>Follow all applicable OSHA standards for your industry (29 CFR 1910 for general industry, 29 CFR 1926 for construction).</p>
            </li>
            <li>
              <h4>Provide Required Training</h4>
              <p>Train workers on hazards they may encounter, proper procedures, and use of protective equipment.</p>
            </li>
            <li>
              <h4>Provide and Pay for PPE</h4>
              <p>Supply necessary personal protective equipment at no cost to employees (with limited exceptions).</p>
            </li>
            <li>
              <h4>Keep Records</h4>
              <p>Maintain required records of work-related injuries and illnesses using OSHA Forms 300, 300A, and 301.</p>
            </li>
          </ol>

          <h3>What Employers Must Post</h3>
          <ul>
            <li>OSHA Job Safety and Health poster (or state equivalent)</li>
            <li>OSHA 300A Summary (February 1 - April 30 each year)</li>
            <li>Any OSHA citations received</li>
          </ul>
        `,
        keyPoints: [
          "General Duty Clause requires a hazard-free workplace",
          "Employers must comply with all applicable OSHA standards",
          "PPE must be provided at no cost to workers",
          "Records of injuries and illnesses must be maintained"
        ]
      },
      {
        id: 4,
        title: "Overview of OSHA Standards (29 CFR)",
        duration: "60 min",
        content: `
          <h3>Understanding the Code of Federal Regulations</h3>
          <p>OSHA standards are published in Title 29 of the Code of Federal Regulations (CFR). These regulations have the force of law and must be followed.</p>

          <h3>Major OSHA Standard Categories</h3>
          <table>
            <tr>
              <th>Part</th>
              <th>Coverage</th>
              <th>Examples</th>
            </tr>
            <tr>
              <td>29 CFR 1903</td>
              <td>Inspections, Citations, Penalties</td>
              <td>How OSHA conducts inspections</td>
            </tr>
            <tr>
              <td>29 CFR 1904</td>
              <td>Recording & Reporting</td>
              <td>OSHA 300 Log requirements</td>
            </tr>
            <tr>
              <td>29 CFR 1910</td>
              <td>General Industry</td>
              <td>Walking surfaces, PPE, hazard communication</td>
            </tr>
            <tr>
              <td>29 CFR 1926</td>
              <td>Construction</td>
              <td>Fall protection, scaffolding, excavations</td>
            </tr>
          </table>

          <h3>Most Frequently Cited Standards</h3>
          <p>Each year, OSHA publishes the most cited standards. Consistently at the top:</p>
          <ol>
            <li>Fall Protection (Construction)</li>
            <li>Hazard Communication</li>
            <li>Respiratory Protection</li>
            <li>Scaffolding (Construction)</li>
            <li>Ladders (Construction)</li>
            <li>Lockout/Tagout</li>
            <li>Powered Industrial Trucks</li>
            <li>Fall Protection Training</li>
            <li>Personal Protective Equipment</li>
            <li>Machine Guarding</li>
          </ol>
        `,
        keyPoints: [
          "OSHA standards are found in Title 29 of the CFR",
          "29 CFR 1910 covers General Industry",
          "29 CFR 1926 covers Construction",
          "Fall protection consistently tops most-cited list"
        ]
      },
      {
        id: 5,
        title: "The Importance of Safety Culture",
        duration: "45 min",
        content: `
          <h3>Beyond Compliance: Building a Safety Culture</h3>
          <p>A true safety culture goes beyond simply following rules. It's when safety becomes a core value—embedded in every decision, process, and behavior throughout an organization.</p>

          <div class="highlight-box">
            <h4>Definition</h4>
            <p><strong>Safety Culture:</strong> The attitudes, beliefs, perceptions, and values that employees share in relation to safety in the workplace.</p>
          </div>

          <h3>Signs of a Strong Safety Culture</h3>
          <ul>
            <li>Leadership visibly prioritizes safety over production</li>
            <li>Workers feel comfortable reporting hazards and near-misses</li>
            <li>Safety suggestions are welcomed and acted upon</li>
            <li>Everyone takes personal responsibility for safety</li>
            <li>Safety training is ongoing, not just check-the-box</li>
            <li>Near-miss reporting is encouraged, not punished</li>
          </ul>

          <h3>The Business Case for Safety</h3>
          <p>Investing in safety isn't just the right thing to do—it makes business sense:</p>
          <ul>
            <li><strong>$1 invested = $4-6 returned</strong> through reduced costs</li>
            <li><strong>Lower workers' compensation premiums</strong></li>
            <li><strong>Reduced turnover</strong> - Workers stay longer at safe workplaces</li>
            <li><strong>Higher productivity</strong> - No downtime from incidents</li>
            <li><strong>Better reputation</strong> - Attracts quality employees and customers</li>
          </ul>
        `,
        keyPoints: [
          "Safety culture means safety is a shared value, not just rules",
          "Leadership must visibly prioritize safety",
          "Near-miss reporting should be encouraged",
          "Every $1 invested in safety returns $4-6 in savings"
        ]
      },
      {
        id: 6,
        title: "Penalties and Citations Explained",
        duration: "40 min",
        content: `
          <h3>OSHA Enforcement</h3>
          <p>OSHA has the authority to inspect workplaces and issue citations for violations. Understanding the penalty structure helps appreciate the importance of compliance.</p>

          <h3>Types of Violations and Maximum Penalties (2024)</h3>
          <table>
            <tr>
              <th>Violation Type</th>
              <th>Maximum Penalty</th>
              <th>Description</th>
            </tr>
            <tr>
              <td>Serious</td>
              <td>$15,625 per violation</td>
              <td>Hazard likely to cause death or serious harm</td>
            </tr>
            <tr>
              <td>Other-Than-Serious</td>
              <td>$15,625 per violation</td>
              <td>Direct relationship to safety but not likely to cause death</td>
            </tr>
            <tr>
              <td>Willful or Repeated</td>
              <td>$156,259 per violation</td>
              <td>Intentional disregard or repeated same violation</td>
            </tr>
            <tr>
              <td>Posting Requirements</td>
              <td>$15,625 per violation</td>
              <td>Failure to post OSHA notices</td>
            </tr>
            <tr>
              <td>Failure to Abate</td>
              <td>$15,625 per day</td>
              <td>Not correcting a cited hazard by deadline</td>
            </tr>
          </table>

          <div class="highlight-box warning">
            <h4>Criminal Penalties</h4>
            <p>In cases of willful violations that result in worker death, criminal prosecution is possible with penalties up to $250,000 and/or 6 months in prison.</p>
          </div>

          <h3>Contesting Citations</h3>
          <p>Employers have the right to contest citations within 15 working days. Options include informal conferences with OSHA or formal hearings before the Occupational Safety and Health Review Commission.</p>
        `,
        keyPoints: [
          "Serious violations can cost up to $15,625 per violation",
          "Willful or repeated violations: up to $156,259 each",
          "Criminal penalties possible for willful violations causing death",
          "Citations can be contested within 15 working days"
        ]
      }
    ],
    summary: "This module laid the foundation for understanding workplace safety in the United States. You've learned about OSHA's history and mission, your fundamental rights as a worker, employer responsibilities, the structure of OSHA standards, and why building a safety culture benefits everyone. These concepts form the basis for all subsequent modules.",
    nextSteps: [
      "Review the OSHA poster at your workplace",
      "Identify who your safety representative is",
      "Familiarize yourself with your company's safety policy",
      "Complete the Module 1 quiz to test your knowledge"
    ]
  },
  2: {
    id: 2,
    title: "Hazard Recognition & Risk Assessment",
    subtitle: "Learning to See Dangers Before They Strike",
    duration: "6-7 hours",
    description: "Master the skills to identify workplace hazards, assess risks, and apply the hierarchy of controls to protect workers.",
    heroImage: "https://images.unsplash.com/photo-1581092160607-ee22621dd758?w=1200",
    objectives: [
      "Classify different types of workplace hazards",
      "Conduct a Job Hazard Analysis (JHA)",
      "Use risk assessment matrices to prioritize hazards",
      "Apply the Hierarchy of Controls effectively",
      "Develop hazard communication strategies"
    ],
    lessons: [
      {
        id: 1,
        title: "Types of Workplace Hazards",
        duration: "50 min",
        content: `
          <h3>The Six Categories of Workplace Hazards</h3>
          <p>Hazards can be found in every workplace. Learning to categorize them helps in developing appropriate controls.</p>

          <div class="hazard-grid">
            <div class="hazard-card">
              <h4>1. Physical Hazards</h4>
              <p>Energy sources that can cause harm through contact or exposure.</p>
              <ul>
                <li>Noise and vibration</li>
                <li>Extreme temperatures</li>
                <li>Radiation (ionizing and non-ionizing)</li>
                <li>Electricity</li>
                <li>Pressure extremes</li>
              </ul>
            </div>
            
            <div class="hazard-card">
              <h4>2. Chemical Hazards</h4>
              <p>Substances that can cause harm through inhalation, absorption, or ingestion.</p>
              <ul>
                <li>Gases and vapors</li>
                <li>Dust and particulates</li>
                <li>Solvents and acids</li>
                <li>Pesticides</li>
                <li>Heavy metals</li>
              </ul>
            </div>
            
            <div class="hazard-card">
              <h4>3. Biological Hazards</h4>
              <p>Living organisms or their byproducts that can cause disease.</p>
              <ul>
                <li>Bacteria and viruses</li>
                <li>Fungi and mold</li>
                <li>Bloodborne pathogens</li>
                <li>Insect vectors</li>
                <li>Animal waste</li>
              </ul>
            </div>
            
            <div class="hazard-card">
              <h4>4. Ergonomic Hazards</h4>
              <p>Conditions that strain the body and can lead to musculoskeletal disorders.</p>
              <ul>
                <li>Repetitive motions</li>
                <li>Awkward postures</li>
                <li>Forceful exertions</li>
                <li>Vibration</li>
                <li>Poor workstation design</li>
              </ul>
            </div>
            
            <div class="hazard-card">
              <h4>5. Safety Hazards</h4>
              <p>Conditions that can cause immediate injury or death.</p>
              <ul>
                <li>Slips, trips, and falls</li>
                <li>Moving machinery</li>
                <li>Falling objects</li>
                <li>Confined spaces</li>
                <li>Fire and explosions</li>
              </ul>
            </div>
            
            <div class="hazard-card">
              <h4>6. Psychosocial Hazards</h4>
              <p>Workplace factors that affect mental health and well-being.</p>
              <ul>
                <li>Workplace violence</li>
                <li>Excessive workload</li>
                <li>Harassment</li>
                <li>Lack of control</li>
                <li>Poor management</li>
              </ul>
            </div>
          </div>
        `,
        keyPoints: [
          "Six main hazard categories: Physical, Chemical, Biological, Ergonomic, Safety, Psychosocial",
          "Physical hazards include noise, temperature, and radiation",
          "Chemical hazards require Safety Data Sheets (SDS)",
          "Ergonomic hazards are often overlooked but cause many injuries"
        ]
      },
      {
        id: 2,
        title: "Conducting a Job Hazard Analysis (JHA)",
        duration: "60 min",
        content: `
          <h3>What is a Job Hazard Analysis?</h3>
          <p>A Job Hazard Analysis (JHA), also called a Job Safety Analysis (JSA), is a technique that focuses on job tasks as a way to identify hazards before they occur.</p>

          <div class="highlight-box">
            <h4>The JHA Process</h4>
            <ol>
              <li><strong>Select the Job</strong> - Prioritize high-risk or new jobs</li>
              <li><strong>Break Down the Job</strong> - List each step in sequence</li>
              <li><strong>Identify Hazards</strong> - Find hazards at each step</li>
              <li><strong>Determine Controls</strong> - Develop preventive measures</li>
            </ol>
          </div>

          <h3>Example JHA: Using a Portable Grinder</h3>
          <table>
            <tr>
              <th>Job Step</th>
              <th>Hazards</th>
              <th>Controls</th>
            </tr>
            <tr>
              <td>1. Inspect grinder</td>
              <td>Cracked or damaged wheel could shatter</td>
              <td>Perform ring test; inspect for cracks; check guard</td>
            </tr>
            <tr>
              <td>2. Secure workpiece</td>
              <td>Loose piece could be thrown; contact with wheel</td>
              <td>Use clamps or vise; position body to side</td>
            </tr>
            <tr>
              <td>3. Start grinding</td>
              <td>Flying particles; noise; sparks/fire</td>
              <td>Wear safety glasses, face shield, hearing protection; clear area of flammables</td>
            </tr>
            <tr>
              <td>4. Complete work</td>
              <td>Hot metal; wheel still spinning</td>
              <td>Allow to cool; wait for wheel to stop before setting down</td>
            </tr>
          </table>

          <h3>When to Conduct/Update a JHA</h3>
          <ul>
            <li>When a new job is introduced</li>
            <li>After an incident or near-miss</li>
            <li>When job procedures change</li>
            <li>When new equipment is used</li>
            <li>As part of regular safety reviews</li>
          </ul>
        `,
        keyPoints: [
          "JHA breaks jobs into steps to identify hazards at each step",
          "Prioritize high-risk jobs for JHA first",
          "Update JHAs after incidents or procedure changes",
          "Involve workers who perform the job in the JHA process"
        ]
      },
      {
        id: 3,
        title: "The Hierarchy of Controls",
        duration: "55 min",
        content: `
          <h3>The Gold Standard for Hazard Control</h3>
          <p>The Hierarchy of Controls is a system for reducing or eliminating workplace hazards. Controls at the top are more effective and protective than those at the bottom.</p>

          <div class="hierarchy-pyramid">
            <div class="level elimination">
              <h4>1. ELIMINATION</h4>
              <p>Physically remove the hazard</p>
              <span class="effectiveness">Most Effective</span>
            </div>
            <div class="level substitution">
              <h4>2. SUBSTITUTION</h4>
              <p>Replace with something safer</p>
            </div>
            <div class="level engineering">
              <h4>3. ENGINEERING CONTROLS</h4>
              <p>Isolate people from the hazard</p>
            </div>
            <div class="level administrative">
              <h4>4. ADMINISTRATIVE CONTROLS</h4>
              <p>Change the way people work</p>
            </div>
            <div class="level ppe">
              <h4>5. PPE</h4>
              <p>Protect the worker</p>
              <span class="effectiveness">Least Effective</span>
            </div>
          </div>

          <h3>Examples of Each Level</h3>
          <table>
            <tr>
              <th>Control Level</th>
              <th>Example</th>
            </tr>
            <tr>
              <td>Elimination</td>
              <td>Remove an unnecessary step that creates a hazard</td>
            </tr>
            <tr>
              <td>Substitution</td>
              <td>Replace a toxic solvent with a water-based cleaner</td>
            </tr>
            <tr>
              <td>Engineering</td>
              <td>Install machine guards, ventilation systems, or barriers</td>
            </tr>
            <tr>
              <td>Administrative</td>
              <td>Job rotation, training, warning signs, procedures</td>
            </tr>
            <tr>
              <td>PPE</td>
              <td>Safety glasses, gloves, respirators, hard hats</td>
            </tr>
          </table>

          <div class="highlight-box warning">
            <h4>Important Note</h4>
            <p>PPE should always be the <strong>last resort</strong>, not the first choice. It depends on the worker using it correctly every time and doesn't eliminate the hazard itself.</p>
          </div>
        `,
        keyPoints: [
          "Elimination is most effective; PPE is least effective",
          "Always try higher-level controls first",
          "Engineering controls don't depend on worker behavior",
          "PPE should be the last line of defense, not the first"
        ]
      }
    ],
    summary: "You've learned to identify the six categories of workplace hazards, conduct systematic Job Hazard Analyses, and apply the Hierarchy of Controls. These skills are fundamental to preventing incidents before they occur.",
    nextSteps: [
      "Practice identifying hazards in your own work area",
      "Suggest a JHA for a task you perform regularly",
      "Think about which controls could be upgraded at your workplace",
      "Complete the Module 2 quiz to test your knowledge"
    ]
  },
  3: {
    id: 3,
    title: "Personal Protective Equipment (PPE)",
    subtitle: "Your Last Line of Defense",
    duration: "5-6 hours",
    description: "Learn to select, use, and maintain PPE properly. Understand when PPE is required and why it's the last line of defense.",
    heroImage: "https://images.unsplash.com/photo-1504328345606-18bbc8c9d7d1?w=1200",
    objectives: [
      "Understand PPE requirements under OSHA",
      "Select appropriate PPE for different hazards",
      "Properly don, doff, and maintain PPE",
      "Recognize PPE limitations",
      "Implement a PPE program"
    ],
    lessons: [
      {
        id: 1,
        title: "PPE Requirements and Selection",
        duration: "50 min",
        content: `
          <h3>OSHA's PPE Standard (29 CFR 1910.132)</h3>
          <p>Personal Protective Equipment is equipment worn to minimize exposure to hazards that cause workplace injuries and illnesses.</p>

          <div class="highlight-box">
            <h4>Employer Responsibilities</h4>
            <ul>
              <li>Assess the workplace for hazards requiring PPE</li>
              <li>Select appropriate PPE for each hazard</li>
              <li>Provide PPE at no cost to employees</li>
              <li>Train employees on proper use</li>
              <li>Ensure PPE is properly maintained</li>
            </ul>
          </div>

          <h3>The PPE Hazard Assessment</h3>
          <p>Before selecting PPE, conduct a thorough hazard assessment:</p>
          <ol>
            <li>Survey the workplace for potential hazards</li>
            <li>Organize and analyze the data</li>
            <li>Select appropriate PPE</li>
            <li>Document the assessment in writing</li>
          </ol>
        `,
        keyPoints: [
          "Employers must provide required PPE at no cost",
          "A written hazard assessment is required",
          "PPE must properly fit each worker",
          "Training on PPE use is mandatory"
        ]
      },
      {
        id: 2,
        title: "Head, Eye, and Face Protection",
        duration: "45 min",
        content: `
          <h3>Hard Hats (29 CFR 1910.135)</h3>
          <p>Hard hats protect against impact, penetration, and electrical hazards to the head.</p>

          <h4>Hard Hat Classes</h4>
          <ul>
            <li><strong>Class E (Electrical)</strong> - Rated for 20,000 volts</li>
            <li><strong>Class G (General)</strong> - Rated for 2,200 volts</li>
            <li><strong>Class C (Conductive)</strong> - No electrical protection</li>
          </ul>

          <h3>Eye and Face Protection (29 CFR 1910.133)</h3>
          <p>Eye injuries are among the most common workplace injuries. Proper protection is essential.</p>

          <div class="highlight-box">
            <h4>ANSI Z87.1 Marking</h4>
            <p>All safety eyewear must be marked with "Z87" to indicate it meets ANSI standards. Look for this marking on the frame and/or lenses.</p>
          </div>

          <table>
            <tr>
              <th>Hazard</th>
              <th>Protection Required</th>
            </tr>
            <tr>
              <td>Flying particles</td>
              <td>Safety glasses with side shields</td>
            </tr>
            <tr>
              <td>Liquid splashes</td>
              <td>Goggles</td>
            </tr>
            <tr>
              <td>Welding</td>
              <td>Welding helmet with proper shade lens</td>
            </tr>
            <tr>
              <td>Grinding, cutting</td>
              <td>Face shield + safety glasses</td>
            </tr>
          </table>
        `,
        keyPoints: [
          "Hard hats have different electrical ratings (E, G, C)",
          "Safety eyewear must be marked with 'Z87'",
          "Face shields should be worn WITH safety glasses",
          "Welding requires specific shade lenses based on process"
        ]
      },
      {
        id: 3,
        title: "Hearing Conservation",
        duration: "50 min",
        content: `
          <h3>Understanding Noise Exposure</h3>
          <p>Hearing loss is permanent and 100% preventable. OSHA requires a hearing conservation program when workers are exposed to 85 dB or more over an 8-hour shift.</p>

          <h4>Common Noise Levels</h4>
          <ul>
            <li>Normal conversation: 60 dB</li>
            <li>Heavy traffic: 85 dB</li>
            <li>Chainsaw: 110 dB</li>
            <li>Jet engine at takeoff: 140 dB</li>
          </ul>

          <div class="highlight-box warning">
            <h4>The 3 dB Rule</h4>
            <p>For every 3 dB increase, the safe exposure time is cut in half:</p>
            <ul>
              <li>85 dB = 8 hours</li>
              <li>88 dB = 4 hours</li>
              <li>91 dB = 2 hours</li>
              <li>94 dB = 1 hour</li>
            </ul>
          </div>

          <h3>Types of Hearing Protection</h3>
          <ul>
            <li><strong>Foam earplugs</strong> - Inexpensive, disposable, NRR 25-33</li>
            <li><strong>Reusable earplugs</strong> - Washable, various styles</li>
            <li><strong>Earmuffs</strong> - Cover entire ear, easy to put on/off</li>
            <li><strong>Canal caps</strong> - For intermittent exposure</li>
          </ul>
        `,
        keyPoints: [
          "Hearing conservation required at 85 dB (8-hour TWA)",
          "Hearing loss is permanent - prevention is key",
          "NRR (Noise Reduction Rating) indicates protection level",
          "Double protection may be needed above 100 dB"
        ]
      }
    ],
    summary: "PPE is your last line of defense against workplace hazards. Understanding proper selection, use, and maintenance is critical for protection. Remember: PPE works only when worn correctly, every time.",
    nextSteps: [
      "Inspect your current PPE for damage or wear",
      "Verify your PPE has proper ratings and certifications",
      "Review your workplace's PPE program",
      "Complete the Module 3 quiz"
    ]
  },
  4: {
    id: 4,
    title: "Fire Safety & Emergency Preparedness",
    subtitle: "When Seconds Count",
    duration: "5-6 hours",
    description: "Learn fire prevention, emergency action planning, and response procedures to protect lives and property.",
    heroImage: "https://images.unsplash.com/photo-1486312338219-ce68d2c6f44d?w=1200",
    objectives: [
      "Understand fire science and prevention",
      "Use fire extinguishers correctly (PASS)",
      "Develop and implement Emergency Action Plans",
      "Know evacuation procedures",
      "Respond appropriately to various emergencies"
    ],
    lessons: [
      {
        id: 1,
        title: "Fire Triangle and Prevention",
        duration: "45 min",
        content: `
          <h3>Understanding Fire</h3>
          <p>To effectively prevent and fight fires, you must understand what makes them burn.</p>

          <div class="fire-triangle">
            <h4>The Fire Triangle</h4>
            <p>Fire requires three elements. Remove any one, and the fire goes out:</p>
            <ul>
              <li><strong>HEAT</strong> - The energy that raises material to ignition temperature</li>
              <li><strong>FUEL</strong> - Any combustible material (solids, liquids, gases)</li>
              <li><strong>OXYGEN</strong> - Air contains about 21% oxygen</li>
            </ul>
          </div>

          <h3>Classes of Fire</h3>
          <table>
            <tr>
              <th>Class</th>
              <th>Fuel Type</th>
              <th>Examples</th>
            </tr>
            <tr>
              <td>Class A</td>
              <td>Ordinary combustibles</td>
              <td>Wood, paper, cloth, trash</td>
            </tr>
            <tr>
              <td>Class B</td>
              <td>Flammable liquids</td>
              <td>Gasoline, oil, grease, paint</td>
            </tr>
            <tr>
              <td>Class C</td>
              <td>Electrical equipment</td>
              <td>Motors, computers, wiring</td>
            </tr>
            <tr>
              <td>Class D</td>
              <td>Combustible metals</td>
              <td>Magnesium, titanium, sodium</td>
            </tr>
            <tr>
              <td>Class K</td>
              <td>Cooking oils</td>
              <td>Deep fryers, kitchen equipment</td>
            </tr>
          </table>
        `,
        keyPoints: [
          "Fire needs heat, fuel, and oxygen to burn",
          "Remove any element to extinguish fire",
          "Different fire classes require different extinguishers",
          "Never use water on electrical or grease fires"
        ]
      },
      {
        id: 2,
        title: "Fire Extinguisher Use (PASS)",
        duration: "40 min",
        content: `
          <h3>When to Fight a Fire</h3>
          <p>Fire extinguishers are for SMALL fires only. Before attempting to fight a fire, ensure:</p>
          <ul>
            <li>The fire alarm has been activated</li>
            <li>The fire is small and contained</li>
            <li>You have an unobstructed escape route</li>
            <li>You have the right type of extinguisher</li>
            <li>You know how to use the extinguisher</li>
          </ul>

          <div class="highlight-box important">
            <h4>The P.A.S.S. Technique</h4>
            <ol>
              <li><strong>P - PULL</strong> the pin to unlock the operating lever</li>
              <li><strong>A - AIM</strong> low, pointing at the base of the fire</li>
              <li><strong>S - SQUEEZE</strong> the lever to discharge the agent</li>
              <li><strong>S - SWEEP</strong> from side to side at the base of the flames</li>
            </ol>
          </div>

          <h3>When to NOT Fight a Fire</h3>
          <ul>
            <li>The fire is spreading rapidly</li>
            <li>You cannot safely escape if needed</li>
            <li>The room is filling with smoke</li>
            <li>Your instincts tell you it's too dangerous</li>
            <li>You don't have the proper extinguisher</li>
          </ul>
        `,
        keyPoints: [
          "PASS: Pull, Aim, Squeeze, Sweep",
          "Only fight small, contained fires",
          "Always have an escape route behind you",
          "When in doubt, get out!"
        ]
      },
      {
        id: 3,
        title: "Emergency Action Plans",
        duration: "55 min",
        content: `
          <h3>OSHA's EAP Requirement (29 CFR 1910.38)</h3>
          <p>An Emergency Action Plan (EAP) describes the actions employees should take during various emergencies.</p>

          <h4>Required EAP Elements</h4>
          <ul>
            <li>Procedures for reporting fires and emergencies</li>
            <li>Emergency evacuation procedures and routes</li>
            <li>Procedures for employees who remain to perform critical operations</li>
            <li>Procedures to account for all employees after evacuation</li>
            <li>Rescue and medical duties for assigned employees</li>
            <li>Names or job titles of contact persons</li>
          </ul>

          <h3>Evacuation Procedures</h3>
          <ol>
            <li>When alarm sounds, stop work immediately</li>
            <li>Follow your designated evacuation route</li>
            <li>Do NOT use elevators</li>
            <li>Close doors behind you to slow fire spread</li>
            <li>Proceed to your assembly point</li>
            <li>Report to your supervisor for accountability</li>
            <li>Do not re-enter until authorized</li>
          </ol>

          <div class="highlight-box">
            <h4>Practice Makes Perfect</h4>
            <p>Fire drills should be conducted at least annually. Regular practice ensures everyone knows what to do when seconds count.</p>
          </div>
        `,
        keyPoints: [
          "Every workplace needs a written EAP",
          "Know your evacuation routes and assembly points",
          "Never use elevators during a fire emergency",
          "Account for all employees after evacuation"
        ]
      }
    ],
    summary: "Fire safety and emergency preparedness can save lives. Understanding fire prevention, knowing how to use extinguishers, and being familiar with your emergency action plan are essential skills for every worker.",
    nextSteps: [
      "Locate fire extinguishers near your work area",
      "Review your workplace's emergency action plan",
      "Know your evacuation route and assembly point",
      "Complete the Module 4 quiz"
    ]
  },
  5: {
    id: 5,
    title: "Electrical Safety",
    subtitle: "Respect the Power",
    duration: "5-6 hours",
    description: "Understand electrical hazards and learn safe work practices to prevent shock, electrocution, and arc flash incidents.",
    heroImage: "https://images.unsplash.com/photo-1621905252507-b35492cc74b4?w=1200",
    objectives: [
      "Recognize electrical hazards",
      "Understand Lockout/Tagout procedures",
      "Use GFCIs and proper grounding",
      "Work safely near electrical equipment",
      "Respond to electrical emergencies"
    ],
    lessons: [
      {
        id: 1,
        title: "Electrical Hazards Overview",
        duration: "45 min",
        content: `
          <h3>The Invisible Danger</h3>
          <p>Electricity is one of the most dangerous workplace hazards because it's invisible. You cannot see, smell, or hear it until it's too late. Understanding electrical hazards is the first step to protecting yourself.</p>

          <div class="highlight-box warning">
            <h4>Sobering Statistics</h4>
            <p>Electrocution is consistently one of OSHA's "Fatal Four" - the four leading causes of death in construction. Approximately <strong>1,000 workers die each year</strong> from electrical injuries in the U.S.</p>
          </div>

          <h3>Types of Electrical Hazards</h3>
          <table>
            <tr>
              <th>Hazard</th>
              <th>Description</th>
              <th>Potential Outcome</th>
            </tr>
            <tr>
              <td>Electric Shock</td>
              <td>Current passes through the body</td>
              <td>Burns, cardiac arrest, death</td>
            </tr>
            <tr>
              <td>Electrocution</td>
              <td>Death caused by electric shock</td>
              <td>Fatal</td>
            </tr>
            <tr>
              <td>Arc Flash</td>
              <td>Explosive release of energy</td>
              <td>Severe burns, blindness, death</td>
            </tr>
            <tr>
              <td>Arc Blast</td>
              <td>Pressure wave from arc flash</td>
              <td>Hearing loss, physical trauma</td>
            </tr>
            <tr>
              <td>Fire</td>
              <td>Ignition from electrical fault</td>
              <td>Property damage, injuries, death</td>
            </tr>
          </table>

          <h3>How Electrical Current Affects the Body</h3>
          <p>The severity of an electrical injury depends on several factors:</p>
          <ul>
            <li><strong>Current (Amperage)</strong> - As little as 100mA can be fatal</li>
            <li><strong>Voltage</strong> - Higher voltage = more current through body</li>
            <li><strong>Resistance</strong> - Wet skin has less resistance than dry</li>
            <li><strong>Path through body</strong> - Hand-to-hand is worst (through heart)</li>
            <li><strong>Duration of contact</strong> - Longer = more damage</li>
          </ul>
        `,
        keyPoints: [
          "Electricity kills approximately 1,000 workers annually in the U.S.",
          "As little as 100mA of current can be fatal",
          "Arc flash can reach temperatures of 35,000°F",
          "Wet conditions significantly increase electrical danger"
        ]
      },
      {
        id: 2,
        title: "Lockout/Tagout (LOTO) Procedures",
        duration: "60 min",
        content: `
          <h3>What is Lockout/Tagout?</h3>
          <p>Lockout/Tagout (LOTO) refers to specific practices and procedures to safeguard employees from the unexpected energization or startup of machinery during service or maintenance.</p>

          <div class="highlight-box important">
            <h4>OSHA Requirement</h4>
            <p>29 CFR 1910.147 - The Control of Hazardous Energy (Lockout/Tagout) requires employers to establish a program to prevent machine startup during maintenance.</p>
          </div>

          <h3>The Six Steps of Lockout/Tagout</h3>
          <ol>
            <li>
              <h4>Preparation</h4>
              <p>Identify all energy sources (electrical, hydraulic, pneumatic, mechanical, thermal, chemical). Review procedures.</p>
            </li>
            <li>
              <h4>Notification</h4>
              <p>Notify all affected employees that equipment will be locked out and why.</p>
            </li>
            <li>
              <h4>Shutdown</h4>
              <p>Shut down the equipment using normal operating procedures.</p>
            </li>
            <li>
              <h4>Isolation</h4>
              <p>Isolate ALL energy sources. This may include multiple disconnect switches, valves, or other devices.</p>
            </li>
            <li>
              <h4>Lockout/Tagout</h4>
              <p>Apply locks and tags to energy isolating devices. Each authorized worker applies their own lock.</p>
            </li>
            <li>
              <h4>Verification</h4>
              <p>Verify zero energy state by testing equipment startup controls. Check for stored energy.</p>
            </li>
          </ol>

          <h3>Key LOTO Requirements</h3>
          <ul>
            <li><strong>Personal locks</strong> - Each worker applies their own lock</li>
            <li><strong>One key per lock</strong> - Only the person who applied the lock has the key</li>
            <li><strong>Standardized tags</strong> - Identify who applied the lock and why</li>
            <li><strong>Never remove another's lock</strong> - Only the person who applied it can remove it</li>
          </ul>

          <div class="highlight-box warning">
            <h4>Critical Warning</h4>
            <p>NEVER work on equipment that is not properly locked and tagged out. "It'll only take a minute" has been the last words of many workers.</p>
          </div>
        `,
        keyPoints: [
          "LOTO prevents unexpected machine startup during maintenance",
          "Six steps: Preparation, Notification, Shutdown, Isolation, Lockout/Tagout, Verification",
          "Each worker must apply their own personal lock",
          "Never remove another person's lock"
        ]
      },
      {
        id: 3,
        title: "GFCIs and Grounding",
        duration: "50 min",
        content: `
          <h3>Ground Fault Circuit Interrupters (GFCIs)</h3>
          <p>A GFCI is a fast-acting circuit breaker designed to shut off electric power when it detects a ground fault - current flowing through an unintended path, such as through a person.</p>

          <h4>How GFCIs Work</h4>
          <p>GFCIs continuously monitor the current flowing through hot and neutral wires. If there's even a small difference (as little as 4-6 milliamps), it means current is leaking somewhere - possibly through a person - and the GFCI trips in 1/40th of a second.</p>

          <div class="highlight-box">
            <h4>Where GFCIs Are Required</h4>
            <ul>
              <li>Bathrooms and kitchens</li>
              <li>Outdoor outlets</li>
              <li>Garages and basements</li>
              <li>Construction sites</li>
              <li>Near water sources</li>
              <li>Any wet or damp location</li>
            </ul>
          </div>

          <h3>Testing GFCIs</h3>
          <p>GFCIs should be tested monthly:</p>
          <ol>
            <li>Press the TEST button - outlet should lose power</li>
            <li>Press RESET to restore power</li>
            <li>If it doesn't trip or reset, replace it immediately</li>
          </ol>

          <h3>Proper Grounding</h3>
          <p>Grounding provides a safe path for fault current to flow to the earth, rather than through a person. Proper grounding:</p>
          <ul>
            <li>Limits voltage on equipment enclosures</li>
            <li>Stabilizes voltage during normal operation</li>
            <li>Facilitates overcurrent device operation</li>
          </ul>

          <h4>Signs of Grounding Problems</h4>
          <ul>
            <li>Tingling when touching equipment</li>
            <li>Flickering lights</li>
            <li>Frequently tripping breakers</li>
            <li>Burning smell from outlets</li>
          </ul>
        `,
        keyPoints: [
          "GFCIs detect current leakage and trip in 1/40th of a second",
          "Test GFCIs monthly using the TEST/RESET buttons",
          "Grounding provides a safe path for fault current",
          "Report any tingling sensations when touching equipment"
        ]
      },
      {
        id: 4,
        title: "Safe Work Practices",
        duration: "55 min",
        content: `
          <h3>Working Safely with Electricity</h3>
          <p>Following safe work practices is essential for anyone who works with or near electrical equipment.</p>

          <h3>The Cardinal Rules of Electrical Safety</h3>
          <ol>
            <li><strong>De-energize first</strong> - Always assume equipment is energized until verified otherwise</li>
            <li><strong>Use proper PPE</strong> - Insulated gloves, safety glasses, arc-rated clothing</li>
            <li><strong>Maintain safe distances</strong> - Know approach boundaries for energized work</li>
            <li><strong>Use insulated tools</strong> - Tools rated for electrical work</li>
            <li><strong>Work with a partner</strong> - Never work alone on electrical systems</li>
          </ol>

          <h3>Before Working on Electrical Equipment</h3>
          <table>
            <tr>
              <th>Step</th>
              <th>Action</th>
            </tr>
            <tr>
              <td>1</td>
              <td>Identify all energy sources</td>
            </tr>
            <tr>
              <td>2</td>
              <td>Notify affected personnel</td>
            </tr>
            <tr>
              <td>3</td>
              <td>De-energize equipment</td>
            </tr>
            <tr>
              <td>4</td>
              <td>Apply LOTO devices</td>
            </tr>
            <tr>
              <td>5</td>
              <td>Verify zero energy state</td>
            </tr>
            <tr>
              <td>6</td>
              <td>Ground conductors if necessary</td>
            </tr>
          </table>

          <div class="highlight-box warning">
            <h4>Common Electrical Safety Violations</h4>
            <ul>
              <li>Using damaged cords or equipment</li>
              <li>Overloading outlets or circuits</li>
              <li>Removing ground prongs from plugs</li>
              <li>Using improper extension cords</li>
              <li>Working on energized equipment without authorization</li>
            </ul>
          </div>
        `,
        keyPoints: [
          "Always assume equipment is energized until verified",
          "Only qualified persons should work on electrical systems",
          "Use insulated tools and proper PPE for electrical work",
          "Never remove ground prongs from plugs"
        ]
      },
      {
        id: 5,
        title: "Arc Flash Protection",
        duration: "50 min",
        content: `
          <h3>What is Arc Flash?</h3>
          <p>An arc flash is a dangerous release of energy caused by an electrical fault. The arc flash can reach temperatures of <strong>35,000°F</strong> - about four times hotter than the surface of the sun.</p>

          <h3>Arc Flash Hazards</h3>
          <ul>
            <li><strong>Heat</strong> - Severe burns from radiant heat</li>
            <li><strong>Light</strong> - Blindness from intense flash</li>
            <li><strong>Pressure</strong> - Arc blast can throw workers and debris</li>
            <li><strong>Sound</strong> - Hearing damage from explosion (up to 140 dB)</li>
            <li><strong>Shrapnel</strong> - Molten metal and debris</li>
          </ul>

          <div class="highlight-box important">
            <h4>Arc Flash Boundaries</h4>
            <p>NFPA 70E defines approach boundaries:</p>
            <ul>
              <li><strong>Flash Protection Boundary</strong> - Area where second-degree burns possible</li>
              <li><strong>Limited Approach</strong> - Unqualified persons need permission</li>
              <li><strong>Restricted Approach</strong> - Qualified persons only with PPE</li>
              <li><strong>Prohibited Approach</strong> - Same as direct contact with energized parts</li>
            </ul>
          </div>

          <h3>Arc-Rated PPE</h3>
          <p>PPE for arc flash protection is rated in calories per square centimeter (cal/cm²):</p>
          <table>
            <tr>
              <th>Category</th>
              <th>Minimum Arc Rating</th>
              <th>Required PPE</th>
            </tr>
            <tr>
              <td>1</td>
              <td>4 cal/cm²</td>
              <td>Arc-rated shirt, pants, safety glasses</td>
            </tr>
            <tr>
              <td>2</td>
              <td>8 cal/cm²</td>
              <td>Arc-rated shirt, pants, face shield, balaclava</td>
            </tr>
            <tr>
              <td>3</td>
              <td>25 cal/cm²</td>
              <td>Arc flash suit, hood, gloves</td>
            </tr>
            <tr>
              <td>4</td>
              <td>40 cal/cm²</td>
              <td>Multi-layer arc flash suit, full hood</td>
            </tr>
          </table>
        `,
        keyPoints: [
          "Arc flash temperatures can reach 35,000°F",
          "Arc-rated PPE is rated in cal/cm² (calories per square centimeter)",
          "Know the approach boundaries for energized equipment",
          "Arc blast pressure wave can throw workers across a room"
        ]
      },
      {
        id: 6,
        title: "Electrical Emergency Response",
        duration: "40 min",
        content: `
          <h3>Responding to Electrical Emergencies</h3>
          <p>Knowing how to respond to electrical emergencies can save lives - including your own.</p>

          <div class="highlight-box warning">
            <h4>If Someone is Being Electrocuted</h4>
            <ol>
              <li><strong>DO NOT TOUCH THEM</strong> - You will become part of the circuit</li>
              <li>Shut off power at the source if possible</li>
              <li>If you cannot shut off power, use a non-conductive object (dry wood, plastic) to separate the person from the source</li>
              <li>Call 911 immediately</li>
              <li>Begin CPR if they are unresponsive and not breathing</li>
            </ol>
          </div>

          <h3>Electrical Fire Response</h3>
          <ul>
            <li>Never use water on an electrical fire</li>
            <li>Use a Class C or ABC fire extinguisher</li>
            <li>If possible, de-energize the equipment first</li>
            <li>Evacuate if the fire cannot be safely controlled</li>
          </ul>

          <h3>Reporting Electrical Hazards</h3>
          <p>Report these conditions immediately:</p>
          <ul>
            <li>Damaged insulation on wires</li>
            <li>Sparking or arcing equipment</li>
            <li>Burning smell from electrical equipment</li>
            <li>Warm or hot outlets or switches</li>
            <li>Flickering lights</li>
            <li>Tingling sensation when touching equipment</li>
            <li>Tripping breakers or blowing fuses frequently</li>
          </ul>

          <h3>First Aid for Electrical Injuries</h3>
          <ul>
            <li>Treat burns with cool water (not ice)</li>
            <li>Look for entry and exit wounds</li>
            <li>Monitor for cardiac issues - internal damage may not be visible</li>
            <li>All electrical shock victims should be evaluated by medical professionals</li>
          </ul>
        `,
        keyPoints: [
          "Never touch someone being electrocuted - shut off power first",
          "Use Class C extinguisher for electrical fires, never water",
          "All electrical shock victims need medical evaluation",
          "Report any electrical hazards immediately"
        ]
      }
    ],
    summary: "Electrical hazards can kill instantly. Understanding LOTO, using GFCIs, and respecting electrical energy are critical for survival.",
    nextSteps: ["Review LOTO procedures at your workplace", "Identify GFCIs in your work area", "Complete Module 5 quiz"]
  },
  6: {
    id: 6,
    title: "Ergonomics & Manual Material Handling",
    subtitle: "Protecting Your Body",
    duration: "5-6 hours",
    description: "Learn proper lifting techniques, workstation ergonomics, and how to prevent musculoskeletal disorders.",
    heroImage: "https://images.unsplash.com/photo-1517245386807-bb43f82c33c4?w=1200",
    objectives: [
      "Understand musculoskeletal disorders (MSDs)",
      "Apply proper lifting techniques",
      "Set up ergonomic workstations",
      "Prevent repetitive strain injuries",
      "Use material handling equipment safely"
    ],
    lessons: [
      {
        id: 1,
        title: "Understanding Musculoskeletal Disorders",
        duration: "45 min",
        content: `
          <h3>What are MSDs?</h3>
          <p>Musculoskeletal Disorders (MSDs) are injuries or disorders affecting muscles, nerves, tendons, joints, cartilage, and spinal discs. They are among the most common workplace injuries.</p>

          <div class="highlight-box">
            <h4>MSD Statistics</h4>
            <ul>
              <li>MSDs account for <strong>33% of all worker injury and illness cases</strong></li>
              <li>Cost employers an estimated <strong>$20 billion per year</strong> in direct costs</li>
              <li>Average MSD case requires <strong>12 days away from work</strong></li>
            </ul>
          </div>

          <h3>Common Types of MSDs</h3>
          <table>
            <tr>
              <th>Condition</th>
              <th>Affected Area</th>
              <th>Common Causes</th>
            </tr>
            <tr>
              <td>Carpal Tunnel Syndrome</td>
              <td>Wrist/Hand</td>
              <td>Repetitive typing, vibrating tools</td>
            </tr>
            <tr>
              <td>Tendinitis</td>
              <td>Tendons (various)</td>
              <td>Repetitive motion, overexertion</td>
            </tr>
            <tr>
              <td>Rotator Cuff Injury</td>
              <td>Shoulder</td>
              <td>Overhead work, repetitive reaching</td>
            </tr>
            <tr>
              <td>Low Back Pain</td>
              <td>Lumbar spine</td>
              <td>Lifting, bending, sitting</td>
            </tr>
            <tr>
              <td>Epicondylitis</td>
              <td>Elbow</td>
              <td>Gripping, twisting motions</td>
            </tr>
          </table>

          <h3>Risk Factors for MSDs</h3>
          <ul>
            <li><strong>Repetition</strong> - Performing the same motion repeatedly</li>
            <li><strong>Force</strong> - Exerting excessive physical effort</li>
            <li><strong>Awkward postures</strong> - Working in positions that strain the body</li>
            <li><strong>Static positions</strong> - Staying in one position too long</li>
            <li><strong>Vibration</strong> - From tools or equipment</li>
            <li><strong>Contact stress</strong> - Body parts pressing against hard surfaces</li>
          </ul>
        `,
        keyPoints: [
          "MSDs account for 33% of all workplace injuries",
          "Risk factors include repetition, force, awkward postures, and vibration",
          "Early reporting of symptoms prevents chronic conditions",
          "MSDs are preventable with proper ergonomic interventions"
        ]
      },
      {
        id: 2,
        title: "Proper Lifting Techniques",
        duration: "55 min",
        content: `
          <h3>The Science of Safe Lifting</h3>
          <p>Back injuries are among the most common workplace injuries. Proper lifting technique protects your spine and muscles from damage.</p>

          <div class="highlight-box important">
            <h4>The Safe Lifting Procedure</h4>
            <ol>
              <li><strong>Plan the lift</strong> - Know where you're going, clear the path</li>
              <li><strong>Test the load</strong> - Push it with your foot to gauge weight</li>
              <li><strong>Get close</strong> - Position yourself close to the object</li>
              <li><strong>Feet shoulder-width apart</strong> - Stable base</li>
              <li><strong>Bend at the knees</strong> - NOT at the waist</li>
              <li><strong>Get a firm grip</strong> - Use handles when available</li>
              <li><strong>Keep back straight</strong> - Maintain natural spine curve</li>
              <li><strong>Lift with your legs</strong> - Push up through your heels</li>
              <li><strong>Keep load close</strong> - Hug the object to your body</li>
              <li><strong>Don't twist</strong> - Turn with your feet, not your back</li>
            </ol>
          </div>

          <h3>When NOT to Lift</h3>
          <ul>
            <li>Object is too heavy (NIOSH recommends max 51 lbs under ideal conditions)</li>
            <li>Object is awkward, unbalanced, or has no handles</li>
            <li>You can't see over or around the load</li>
            <li>Path is unclear or slippery</li>
            <li>You're fatigued or have an existing injury</li>
          </ul>

          <h3>Team Lifting</h3>
          <p>When a load requires two or more people:</p>
          <ul>
            <li>Designate one person as the leader</li>
            <li>Lift and lower together on a count</li>
            <li>Match heights when possible (taller person at heavier end)</li>
            <li>Communicate throughout the lift</li>
          </ul>

          <div class="highlight-box warning">
            <h4>Common Lifting Mistakes</h4>
            <ul>
              <li>Bending at the waist instead of knees</li>
              <li>Twisting while lifting</li>
              <li>Reaching while holding a load</li>
              <li>Lifting above shoulder height</li>
              <li>Jerking or sudden movements</li>
            </ul>
          </div>
        `,
        keyPoints: [
          "Bend at the knees, not the waist",
          "Keep the load close to your body",
          "Never twist while carrying a load",
          "Ask for help or use equipment for heavy/awkward loads"
        ]
      },
      {
        id: 3,
        title: "Workstation Ergonomics",
        duration: "50 min",
        content: `
          <h3>Setting Up an Ergonomic Workstation</h3>
          <p>For the millions of workers who spend hours at computer workstations, proper setup is crucial for preventing MSDs.</p>

          <h3>Monitor Positioning</h3>
          <ul>
            <li><strong>Height</strong> - Top of screen at or slightly below eye level</li>
            <li><strong>Distance</strong> - Arm's length away (18-24 inches)</li>
            <li><strong>Angle</strong> - Tilted slightly upward (10-20°)</li>
            <li><strong>Glare</strong> - Position to minimize screen reflections</li>
          </ul>

          <h3>Chair Adjustment</h3>
          <ul>
            <li><strong>Seat height</strong> - Feet flat on floor, thighs parallel to ground</li>
            <li><strong>Seat depth</strong> - 2-3 finger widths between seat edge and back of knee</li>
            <li><strong>Lumbar support</strong> - Supports natural curve of lower back</li>
            <li><strong>Armrests</strong> - Shoulders relaxed, elbows at 90°</li>
          </ul>

          <h3>Keyboard and Mouse</h3>
          <table>
            <tr>
              <th>Element</th>
              <th>Correct Position</th>
            </tr>
            <tr>
              <td>Keyboard height</td>
              <td>Elbows at 90° angle, wrists straight</td>
            </tr>
            <tr>
              <td>Keyboard angle</td>
              <td>Flat or slightly negative tilt</td>
            </tr>
            <tr>
              <td>Mouse location</td>
              <td>Same level as keyboard, within easy reach</td>
            </tr>
            <tr>
              <td>Wrist position</td>
              <td>Neutral, not bent up or down</td>
            </tr>
          </table>

          <div class="highlight-box">
            <h4>The 20-20-20 Rule</h4>
            <p>Every <strong>20 minutes</strong>, look at something <strong>20 feet away</strong> for <strong>20 seconds</strong>. This reduces eye strain from computer work.</p>
          </div>
        `,
        keyPoints: [
          "Monitor top should be at or slightly below eye level",
          "Feet should be flat on floor, thighs parallel to ground",
          "Wrists should be straight when typing, not bent",
          "Follow the 20-20-20 rule to reduce eye strain"
        ]
      },
      {
        id: 4,
        title: "Preventing Repetitive Strain Injuries",
        duration: "45 min",
        content: `
          <h3>What are Repetitive Strain Injuries?</h3>
          <p>Repetitive Strain Injuries (RSIs) develop gradually from repeated stress on the same body parts. They're often called cumulative trauma disorders because damage builds up over time.</p>

          <h3>Warning Signs of RSIs</h3>
          <ul>
            <li>Tingling or numbness in hands/fingers</li>
            <li>Aching or burning sensation</li>
            <li>Loss of grip strength</li>
            <li>Pain that worsens with activity</li>
            <li>Stiffness, especially in the morning</li>
            <li>Swelling or tenderness</li>
          </ul>

          <div class="highlight-box warning">
            <h4>Early Intervention is Critical</h4>
            <p>RSIs are much easier to treat in early stages. If you notice symptoms, report them immediately. Ignoring early warning signs can lead to permanent damage.</p>
          </div>

          <h3>Prevention Strategies</h3>
          <ol>
            <li><strong>Take frequent breaks</strong> - Microbreaks every 20-30 minutes</li>
            <li><strong>Stretch regularly</strong> - Wrist circles, shoulder rolls, neck stretches</li>
            <li><strong>Vary your tasks</strong> - Alternate between different activities</li>
            <li><strong>Use proper technique</strong> - Neutral wrist position, light touch on keyboard</li>
            <li><strong>Use ergonomic tools</strong> - Ergonomic keyboard, vertical mouse</li>
            <li><strong>Maintain fitness</strong> - Strong muscles resist injury better</li>
          </ol>

          <h3>Desk Stretches</h3>
          <ul>
            <li><strong>Wrist flexion/extension</strong> - Gently bend wrist up and down</li>
            <li><strong>Finger spreads</strong> - Spread fingers wide, then make a fist</li>
            <li><strong>Shoulder shrugs</strong> - Raise shoulders to ears, hold, release</li>
            <li><strong>Neck rolls</strong> - Slowly roll head in circles</li>
            <li><strong>Standing stretch</strong> - Stand up and reach for the ceiling</li>
          </ul>
        `,
        keyPoints: [
          "RSIs develop gradually - early symptoms should not be ignored",
          "Take microbreaks every 20-30 minutes",
          "Stretching and varying tasks helps prevent RSIs",
          "Report symptoms early for best treatment outcomes"
        ]
      },
      {
        id: 5,
        title: "Material Handling Equipment",
        duration: "50 min",
        content: `
          <h3>Using Equipment to Reduce Manual Handling</h3>
          <p>When loads are too heavy or awkward for manual lifting, material handling equipment can protect workers from injury.</p>

          <h3>Common Material Handling Equipment</h3>
          <table>
            <tr>
              <th>Equipment</th>
              <th>Use</th>
              <th>Key Safety Points</th>
            </tr>
            <tr>
              <td>Hand truck/Dolly</td>
              <td>Moving boxes, small loads</td>
              <td>Keep load low, tilt back, push don't pull</td>
            </tr>
            <tr>
              <td>Pallet jack</td>
              <td>Moving palletized loads</td>
              <td>Check load stability, watch for pedestrians</td>
            </tr>
            <tr>
              <td>Forklift</td>
              <td>Heavy/palletized loads</td>
              <td>Requires training and certification</td>
            </tr>
            <tr>
              <td>Conveyor</td>
              <td>Continuous material flow</td>
              <td>Never climb on, use emergency stops</td>
            </tr>
            <tr>
              <td>Hoist/Crane</td>
              <td>Very heavy loads</td>
              <td>Inspect before use, stay out of load path</td>
            </tr>
          </table>

          <h3>Forklift Safety Basics</h3>
          <ul>
            <li>Only trained, certified operators may use forklifts</li>
            <li>Wear seatbelt at all times</li>
            <li>Travel with forks lowered</li>
            <li>Never exceed rated capacity</li>
            <li>Sound horn at intersections</li>
            <li>Never allow riders on forks</li>
          </ul>

          <div class="highlight-box">
            <h4>The Engineering Solution</h4>
            <p>Using mechanical equipment is an <strong>engineering control</strong> - one of the most effective ways to reduce MSD risk according to the Hierarchy of Controls.</p>
          </div>

          <h3>Choosing the Right Equipment</h3>
          <p>Consider these factors when selecting material handling equipment:</p>
          <ul>
            <li>Weight and dimensions of load</li>
            <li>Distance to be moved</li>
            <li>Frequency of handling</li>
            <li>Path conditions (stairs, ramps, surfaces)</li>
            <li>Space constraints</li>
          </ul>
        `,
        keyPoints: [
          "Use equipment when loads exceed safe manual lifting limits",
          "Only trained/certified operators should use forklifts",
          "Using equipment is an engineering control - very effective",
          "Match equipment to the task requirements"
        ]
      }
    ],
    summary: "Ergonomic hazards cause many workplace injuries. Proper techniques and workstation setup prevent MSDs.",
    nextSteps: ["Assess your workstation ergonomics", "Practice proper lifting techniques", "Complete Module 6 quiz"]
  },
  7: {
    id: 7,
    title: "Specialized Safety Topics",
    subtitle: "Advanced Hazard Management",
    duration: "7-8 hours",
    description: "Dive into specialized topics including fall protection, confined spaces, machine guarding, and environmental hazards.",
    heroImage: "https://images.unsplash.com/photo-1504307651254-35680f356dfd?w=1200",
    objectives: [
      "Implement fall protection systems",
      "Recognize confined space hazards",
      "Ensure proper machine guarding",
      "Prevent bloodborne pathogen exposure",
      "Manage heat and cold stress"
    ],
    lessons: [
      {
        id: 1,
        title: "Fall Protection",
        duration: "60 min",
        content: `
          <h3>The #1 Killer in Construction</h3>
          <p>Falls are consistently the leading cause of death in construction and a major cause of injuries in all industries. Proper fall protection saves lives.</p>

          <div class="highlight-box warning">
            <h4>OSHA Fall Protection Requirements</h4>
            <ul>
              <li><strong>General Industry</strong> - Fall protection required at 4 feet</li>
              <li><strong>Construction</strong> - Fall protection required at 6 feet</li>
              <li><strong>Shipyards</strong> - Fall protection required at 5 feet</li>
            </ul>
          </div>

          <h3>Types of Fall Protection</h3>
          <table>
            <tr>
              <th>Type</th>
              <th>How It Works</th>
              <th>Best Used For</th>
            </tr>
            <tr>
              <td>Guardrails</td>
              <td>Physical barrier prevents falls</td>
              <td>Fixed work areas, platforms</td>
            </tr>
            <tr>
              <td>Safety Nets</td>
              <td>Catches falling workers</td>
              <td>Large open areas, bridges</td>
            </tr>
            <tr>
              <td>Personal Fall Arrest</td>
              <td>Harness stops fall in progress</td>
              <td>Mobile work, varied locations</td>
            </tr>
            <tr>
              <td>Positioning Systems</td>
              <td>Keeps worker in position</td>
              <td>Tower climbing, pole work</td>
            </tr>
            <tr>
              <td>Travel Restraint</td>
              <td>Prevents reaching fall edge</td>
              <td>Roof work, open edges</td>
            </tr>
          </table>

          <h3>Personal Fall Arrest System Components</h3>
          <ul>
            <li><strong>Full body harness</strong> - Distributes fall forces across body</li>
            <li><strong>Lanyard or SRL</strong> - Connects harness to anchor</li>
            <li><strong>Anchor point</strong> - Must support 5,000 lbs per worker</li>
            <li><strong>Connectors</strong> - Carabiners, D-rings, snap hooks</li>
          </ul>

          <div class="highlight-box important">
            <h4>Fall Clearance Calculation</h4>
            <p>Before using fall arrest, calculate the total fall distance to ensure you won't hit the ground:</p>
            <p>Free fall (6 ft max) + Deceleration distance (3.5 ft) + Harness stretch (1 ft) + Worker height (6 ft) + Safety factor (2 ft) = <strong>18.5 feet minimum clearance needed</strong></p>
          </div>
        `,
        keyPoints: [
          "Falls are the leading cause of death in construction",
          "General industry requires protection at 4 feet",
          "Anchor points must support 5,000 lbs per worker",
          "Calculate fall clearance before using fall arrest"
        ]
      },
      {
        id: 2,
        title: "Confined Space Entry",
        duration: "65 min",
        content: `
          <h3>What is a Confined Space?</h3>
          <p>A confined space has three characteristics:</p>
          <ol>
            <li>Large enough for a worker to enter and perform work</li>
            <li>Has limited or restricted entry/exit</li>
            <li>Is not designed for continuous occupancy</li>
          </ol>

          <h3>Examples of Confined Spaces</h3>
          <ul>
            <li>Storage tanks and vessels</li>
            <li>Silos and bins</li>
            <li>Manholes and sewers</li>
            <li>Pits and vaults</li>
            <li>Pipelines</li>
            <li>Boilers and furnaces</li>
          </ul>

          <h3>Permit-Required Confined Space</h3>
          <p>A confined space becomes "permit-required" if it has one or more of these hazards:</p>
          <ul>
            <li>Hazardous atmosphere (toxic, flammable, oxygen-deficient)</li>
            <li>Engulfment hazard (grain, sand, water)</li>
            <li>Entrapment hazard (inwardly converging walls)</li>
            <li>Any other serious safety/health hazard</li>
          </ul>

          <div class="highlight-box warning">
            <h4>Atmospheric Hazards</h4>
            <table>
              <tr>
                <th>Hazard</th>
                <th>Danger Level</th>
              </tr>
              <tr>
                <td>Oxygen deficient</td>
                <td>Below 19.5%</td>
              </tr>
              <tr>
                <td>Oxygen enriched</td>
                <td>Above 23.5%</td>
              </tr>
              <tr>
                <td>Flammable gas</td>
                <td>Above 10% of LEL</td>
              </tr>
              <tr>
                <td>Toxic substances</td>
                <td>Above PEL</td>
              </tr>
            </table>
          </div>

          <h3>Entry Permit Requirements</h3>
          <ul>
            <li>Space identification and location</li>
            <li>Purpose of entry</li>
            <li>Authorized entrants and attendants</li>
            <li>Atmospheric test results</li>
            <li>Rescue procedures</li>
            <li>Communication procedures</li>
            <li>Entry supervisor signature</li>
          </ul>
        `,
        keyPoints: [
          "Confined spaces have limited entry/exit and aren't for continuous occupancy",
          "Test atmosphere before EVERY entry",
          "Attendant must remain outside at all times",
          "Have rescue procedures in place before entry"
        ]
      },
      {
        id: 3,
        title: "Machine Guarding",
        duration: "55 min",
        content: `
          <h3>Why Machine Guarding Matters</h3>
          <p>Unguarded or inadequately guarded machines cause approximately 18,000 amputations, lacerations, crushing injuries, and abrasions each year.</p>

          <h3>Hazardous Machine Motions</h3>
          <table>
            <tr>
              <th>Motion Type</th>
              <th>Description</th>
              <th>Example</th>
            </tr>
            <tr>
              <td>Rotating</td>
              <td>Circular movement</td>
              <td>Shafts, pulleys, flywheels</td>
            </tr>
            <tr>
              <td>Reciprocating</td>
              <td>Back and forth</td>
              <td>Presses, saws</td>
            </tr>
            <tr>
              <td>Transverse</td>
              <td>Straight line</td>
              <td>Conveyors, belts</td>
            </tr>
          </table>

          <h3>Hazardous Actions</h3>
          <ul>
            <li><strong>Cutting</strong> - Saws, drills, milling machines</li>
            <li><strong>Punching</strong> - Power presses</li>
            <li><strong>Shearing</strong> - Hydraulic shears, guillotines</li>
            <li><strong>Bending</strong> - Press brakes</li>
            <li><strong>In-running nip points</strong> - Where rotating parts meet</li>
          </ul>

          <div class="highlight-box">
            <h4>Requirements for Effective Guards</h4>
            <ul>
              <li>Prevent contact with dangerous parts</li>
              <li>Be secure and not easily removed</li>
              <li>Create no new hazards</li>
              <li>Allow safe lubrication and maintenance</li>
              <li>Not interfere with machine operation</li>
            </ul>
          </div>

          <h3>Types of Machine Guards</h3>
          <ul>
            <li><strong>Fixed guards</strong> - Permanent barrier, most reliable</li>
            <li><strong>Interlocked guards</strong> - Machine stops when guard opens</li>
            <li><strong>Adjustable guards</strong> - Flexible for different operations</li>
            <li><strong>Self-adjusting guards</strong> - Moves with stock being fed</li>
          </ul>

          <div class="highlight-box warning">
            <h4>Never Remove or Bypass Guards</h4>
            <p>If a guard makes work difficult, report it. Never operate a machine with guards removed or disabled - even for "just a minute."</p>
          </div>
        `,
        keyPoints: [
          "Unguarded machines cause 18,000 injuries annually",
          "Guards must prevent contact and be secure",
          "Never remove or bypass machine guards",
          "Report guard problems - don't work around them"
        ]
      },
      {
        id: 4,
        title: "Bloodborne Pathogens",
        duration: "50 min",
        content: `
          <h3>What are Bloodborne Pathogens?</h3>
          <p>Bloodborne pathogens are infectious microorganisms in human blood that can cause disease. The main concerns are:</p>
          <ul>
            <li><strong>Hepatitis B (HBV)</strong> - Attacks the liver, vaccine available</li>
            <li><strong>Hepatitis C (HCV)</strong> - Attacks the liver, no vaccine</li>
            <li><strong>HIV</strong> - Attacks immune system, no vaccine</li>
          </ul>

          <h3>Who is at Risk?</h3>
          <p>Workers with occupational exposure include:</p>
          <ul>
            <li>Healthcare workers</li>
            <li>Emergency responders</li>
            <li>Housekeeping staff</li>
            <li>Laundry workers</li>
            <li>Laboratory personnel</li>
            <li>Anyone who might encounter blood or body fluids</li>
          </ul>

          <div class="highlight-box important">
            <h4>Universal Precautions</h4>
            <p>Treat ALL blood and body fluids as if they are infectious. You cannot tell by looking whether blood is contaminated.</p>
          </div>

          <h3>Exposure Prevention</h3>
          <ul>
            <li>Use appropriate PPE (gloves, gowns, eye protection)</li>
            <li>Never recap needles - use sharps containers</li>
            <li>Clean up blood spills with proper disinfectant</li>
            <li>Wash hands immediately after removing gloves</li>
            <li>Get the Hepatitis B vaccine (employer-provided)</li>
          </ul>

          <h3>If Exposure Occurs</h3>
          <ol>
            <li>Wash the area thoroughly with soap and water</li>
            <li>Flush eyes/mouth with water if splashed</li>
            <li>Report exposure immediately to supervisor</li>
            <li>Seek medical evaluation promptly</li>
            <li>Document the incident</li>
          </ol>
        `,
        keyPoints: [
          "Treat all blood as potentially infectious",
          "Use appropriate PPE when exposure is possible",
          "Hepatitis B vaccine is available and employer-provided",
          "Report any exposure immediately"
        ]
      },
      {
        id: 5,
        title: "Heat and Cold Stress",
        duration: "55 min",
        content: `
          <h3>Heat-Related Illnesses</h3>
          <p>Working in hot environments can overwhelm the body's ability to cool itself, leading to serious illness or death.</p>

          <h3>Progression of Heat Illness</h3>
          <table>
            <tr>
              <th>Condition</th>
              <th>Symptoms</th>
              <th>Response</th>
            </tr>
            <tr>
              <td>Heat Rash</td>
              <td>Red bumps on skin</td>
              <td>Rest in cool area, keep skin dry</td>
            </tr>
            <tr>
              <td>Heat Cramps</td>
              <td>Painful muscle spasms</td>
              <td>Rest, drink water, light stretching</td>
            </tr>
            <tr>
              <td>Heat Exhaustion</td>
              <td>Heavy sweating, weakness, nausea</td>
              <td>Move to cool area, remove excess clothing, hydrate</td>
            </tr>
            <tr>
              <td>Heat Stroke</td>
              <td>Hot dry skin, confusion, seizures</td>
              <td>MEDICAL EMERGENCY - Call 911, cool immediately</td>
            </tr>
          </table>

          <div class="highlight-box warning">
            <h4>Heat Stroke Warning Signs</h4>
            <p>Heat stroke is life-threatening. Signs include:</p>
            <ul>
              <li>Hot, dry skin (no sweating)</li>
              <li>Confusion or altered mental state</li>
              <li>Slurred speech</li>
              <li>Loss of consciousness</li>
              <li>Seizures</li>
            </ul>
            <p><strong>Call 911 immediately and begin cooling!</strong></p>
          </div>

          <h3>Preventing Heat Illness</h3>
          <ul>
            <li><strong>Water</strong> - Drink before you're thirsty, 1 cup every 15-20 min</li>
            <li><strong>Rest</strong> - Take breaks in shade or air conditioning</li>
            <li><strong>Shade</strong> - Provide shaded work areas when possible</li>
            <li><strong>Acclimatization</strong> - Gradually increase workload in heat</li>
          </ul>

          <h3>Cold Stress</h3>
          <p>Working in cold environments brings its own hazards:</p>
          <ul>
            <li><strong>Hypothermia</strong> - Body temperature drops dangerously low</li>
            <li><strong>Frostbite</strong> - Freezing of skin and tissue</li>
            <li><strong>Trench foot</strong> - Prolonged wet, cold exposure</li>
          </ul>

          <h3>Cold Prevention</h3>
          <ul>
            <li>Wear layered, insulated clothing</li>
            <li>Cover extremities (head, hands, feet)</li>
            <li>Take warm-up breaks</li>
            <li>Work in pairs to monitor each other</li>
            <li>Avoid exhaustion and stay dry</li>
          </ul>
        `,
        keyPoints: [
          "Heat stroke is a medical emergency - call 911",
          "Drink water before you feel thirsty",
          "Allow time for acclimatization to hot conditions",
          "In cold, dress in layers and protect extremities"
        ]
      },
      {
        id: 6,
        title: "Chemical Safety and SDS",
        duration: "50 min",
        content: `
          <h3>Hazard Communication Standard</h3>
          <p>OSHA's Hazard Communication Standard (HazCom) gives workers the "Right to Know" about chemical hazards in their workplace.</p>

          <h3>Key HazCom Requirements</h3>
          <ul>
            <li>Written hazard communication program</li>
            <li>Safety Data Sheets (SDS) for all hazardous chemicals</li>
            <li>Proper labeling of containers</li>
            <li>Employee training</li>
          </ul>

          <h3>Understanding Safety Data Sheets</h3>
          <p>SDS contain 16 standardized sections:</p>
          <ol>
            <li>Identification</li>
            <li>Hazard identification</li>
            <li>Composition/ingredients</li>
            <li>First-aid measures</li>
            <li>Fire-fighting measures</li>
            <li>Accidental release measures</li>
            <li>Handling and storage</li>
            <li>Exposure controls/PPE</li>
            <li>Physical and chemical properties</li>
            <li>Stability and reactivity</li>
            <li>Toxicological information</li>
            <li>Ecological information</li>
            <li>Disposal considerations</li>
            <li>Transport information</li>
            <li>Regulatory information</li>
            <li>Other information</li>
          </ol>

          <div class="highlight-box">
            <h4>GHS Pictograms</h4>
            <p>The Globally Harmonized System uses standard pictograms to identify hazards:</p>
            <ul>
              <li>🔥 Flame - Flammables</li>
              <li>💀 Skull - Acute toxicity</li>
              <li>⚠️ Exclamation - Irritant/harmful</li>
              <li>🧪 Corrosion - Corrosive</li>
              <li>🌍 Environment - Environmental hazard</li>
            </ul>
          </div>

          <h3>Label Requirements</h3>
          <p>Chemical labels must include:</p>
          <ul>
            <li>Product identifier (name)</li>
            <li>Signal word (Danger or Warning)</li>
            <li>Hazard statements</li>
            <li>Precautionary statements</li>
            <li>Pictograms</li>
            <li>Supplier information</li>
          </ul>
        `,
        keyPoints: [
          "SDS must be available for all hazardous chemicals",
          "Know the location of SDS in your workplace",
          "Never use chemicals without reading the label",
          "GHS pictograms provide quick hazard identification"
        ]
      }
    ],
    summary: "Specialized hazards require specialized knowledge. This module covers critical topics for high-risk work environments.",
    nextSteps: ["Identify specialized hazards in your work", "Review relevant procedures", "Complete Module 7 quiz"]
  },
  8: {
    id: 8,
    title: "Compliance, Recordkeeping & Certification",
    subtitle: "Bringing It All Together",
    duration: "5-6 hours",
    description: "Master OSHA recordkeeping requirements, incident investigation, and prepare for your final certification.",
    heroImage: "https://images.unsplash.com/photo-1450101499163-c8848c66ca85?w=1200",
    objectives: [
      "Maintain OSHA 300 Log correctly",
      "Conduct effective incident investigations",
      "Develop safety inspection checklists",
      "Create workplace safety policies",
      "Pass the final certification exam"
    ],
    lessons: [
      {
        id: 1,
        title: "OSHA Recordkeeping Requirements",
        duration: "55 min",
        content: `
          <h3>Why Recordkeeping Matters</h3>
          <p>OSHA requires most employers to keep records of work-related injuries and illnesses. These records help identify patterns, measure safety performance, and ensure compliance.</p>

          <h3>Who Must Keep Records?</h3>
          <p>Employers with <strong>more than 10 employees</strong> must maintain OSHA injury and illness records, unless they're in a partially exempt industry (certain retail, service, and other low-hazard industries).</p>

          <h3>The OSHA 300 Log</h3>
          <p>Form 300 is the Log of Work-Related Injuries and Illnesses. It includes:</p>
          <ul>
            <li>Case number</li>
            <li>Employee name (may be kept private for sensitive cases)</li>
            <li>Job title</li>
            <li>Date of injury/illness</li>
            <li>Description of injury/illness</li>
            <li>Classification (death, days away, restricted work, other)</li>
          </ul>

          <div class="highlight-box">
            <h4>What is Recordable?</h4>
            <p>An injury or illness is recordable if it results in:</p>
            <ul>
              <li>Death</li>
              <li>Days away from work</li>
              <li>Restricted work or job transfer</li>
              <li>Medical treatment beyond first aid</li>
              <li>Loss of consciousness</li>
              <li>Significant injury or illness diagnosed by a physician</li>
            </ul>
          </div>

          <h3>OSHA Forms</h3>
          <table>
            <tr>
              <th>Form</th>
              <th>Purpose</th>
              <th>Requirements</th>
            </tr>
            <tr>
              <td>300</td>
              <td>Log of injuries/illnesses</td>
              <td>Maintain year-round, keep 5 years</td>
            </tr>
            <tr>
              <td>300A</td>
              <td>Annual Summary</td>
              <td>Post Feb 1 - Apr 30</td>
            </tr>
            <tr>
              <td>301</td>
              <td>Individual incident report</td>
              <td>Complete within 7 days of incident</td>
            </tr>
          </table>

          <h3>Electronic Submission</h3>
          <p>Employers with 250+ employees (or 20-249 in certain high-risk industries) must electronically submit Form 300A data to OSHA annually.</p>
        `,
        keyPoints: [
          "Employers with 10+ employees must keep injury/illness records",
          "Form 300 is the main injury log, 301 is for individual incidents",
          "300A Summary must be posted February 1 - April 30",
          "Records must be retained for 5 years"
        ]
      },
      {
        id: 2,
        title: "Incident Investigation",
        duration: "60 min",
        content: `
          <h3>The Purpose of Incident Investigation</h3>
          <p>The goal is NOT to assign blame, but to identify root causes and prevent recurrence. A thorough investigation answers:</p>
          <ul>
            <li>What happened?</li>
            <li>Why did it happen?</li>
            <li>What can prevent it from happening again?</li>
          </ul>

          <div class="highlight-box important">
            <h4>Near-Miss Reporting</h4>
            <p>Investigate near-misses too! They provide valuable learning opportunities without the tragedy of an actual injury.</p>
          </div>

          <h3>Investigation Steps</h3>
          <ol>
            <li><strong>Secure the scene</strong> - Prevent further injuries, preserve evidence</li>
            <li><strong>Provide medical care</strong> - Ensure injured workers receive treatment</li>
            <li><strong>Gather facts</strong> - Interview witnesses, take photos, collect physical evidence</li>
            <li><strong>Identify root causes</strong> - Use the "5 Whys" or other analysis tools</li>
            <li><strong>Develop corrective actions</strong> - Address root causes, not just symptoms</li>
            <li><strong>Implement and follow up</strong> - Ensure actions are completed and effective</li>
          </ol>

          <h3>The "5 Whys" Technique</h3>
          <p>Keep asking "why" to dig deeper into root causes:</p>
          <div class="highlight-box">
            <p><strong>Example:</strong></p>
            <p>Worker slipped and fell → <em>Why?</em></p>
            <p>Floor was wet → <em>Why?</em></p>
            <p>Pipe was leaking → <em>Why?</em></p>
            <p>Maintenance was overdue → <em>Why?</em></p>
            <p>No preventive maintenance program → <em>ROOT CAUSE</em></p>
          </div>

          <h3>Common Investigation Mistakes</h3>
          <ul>
            <li>Focusing on blame instead of causes</li>
            <li>Stopping at the first "why"</li>
            <li>Not interviewing all witnesses</li>
            <li>Implementing quick fixes without addressing root causes</li>
            <li>Not following up on corrective actions</li>
          </ul>
        `,
        keyPoints: [
          "Focus on root causes, not blame",
          "Use the 5 Whys technique to dig deeper",
          "Investigate near-misses, not just injuries",
          "Follow up to ensure corrective actions are effective"
        ]
      },
      {
        id: 3,
        title: "Safety Inspections",
        duration: "50 min",
        content: `
          <h3>Types of Safety Inspections</h3>
          <ul>
            <li><strong>Routine inspections</strong> - Regular, scheduled walkthroughs</li>
            <li><strong>Pre-task inspections</strong> - Before starting specific work</li>
            <li><strong>Special inspections</strong> - After incidents or process changes</li>
            <li><strong>Compliance audits</strong> - Verify regulatory compliance</li>
          </ul>

          <h3>Creating Effective Checklists</h3>
          <p>A good inspection checklist:</p>
          <ul>
            <li>Is specific to your workplace and operations</li>
            <li>Covers all relevant hazard categories</li>
            <li>Includes space for notes and observations</li>
            <li>Has clear pass/fail criteria</li>
            <li>Is updated regularly based on findings</li>
          </ul>

          <h3>Sample Inspection Categories</h3>
          <table>
            <tr>
              <th>Area</th>
              <th>Items to Check</th>
            </tr>
            <tr>
              <td>Walking surfaces</td>
              <td>Spills, trip hazards, guardrails, floor condition</td>
            </tr>
            <tr>
              <td>Electrical</td>
              <td>Cord condition, GFCI function, panel access</td>
            </tr>
            <tr>
              <td>Fire safety</td>
              <td>Extinguisher inspection, exit access, alarms</td>
            </tr>
            <tr>
              <td>PPE</td>
              <td>Availability, condition, proper use</td>
            </tr>
            <tr>
              <td>Machinery</td>
              <td>Guards in place, LOTO available, condition</td>
            </tr>
            <tr>
              <td>Housekeeping</td>
              <td>Clutter, storage, waste disposal</td>
            </tr>
          </table>

          <div class="highlight-box">
            <h4>Inspection Best Practices</h4>
            <ul>
              <li>Involve workers - they know where hazards are</li>
              <li>Document everything - photos are valuable</li>
              <li>Prioritize findings by risk level</li>
              <li>Set deadlines for corrective actions</li>
              <li>Track completion of corrections</li>
            </ul>
          </div>
        `,
        keyPoints: [
          "Regular inspections catch hazards before they cause injuries",
          "Use checklists tailored to your workplace",
          "Involve workers in the inspection process",
          "Track and follow up on corrective actions"
        ]
      },
      {
        id: 4,
        title: "Creating Safety Policies",
        duration: "55 min",
        content: `
          <h3>Elements of an Effective Safety Program</h3>
          <p>A comprehensive safety program includes:</p>
          <ol>
            <li><strong>Management commitment</strong> - Visible leadership support</li>
            <li><strong>Employee involvement</strong> - Worker participation in safety</li>
            <li><strong>Hazard identification</strong> - Systematic hazard assessment</li>
            <li><strong>Hazard prevention and control</strong> - Elimination or control of hazards</li>
            <li><strong>Training</strong> - Education on hazards and safe practices</li>
            <li><strong>Evaluation</strong> - Measuring program effectiveness</li>
          </ol>

          <h3>Written Safety Policy</h3>
          <p>Your safety policy should include:</p>
          <ul>
            <li>Statement of commitment from top management</li>
            <li>Safety goals and objectives</li>
            <li>Roles and responsibilities</li>
            <li>Reporting procedures</li>
            <li>Consequences for violations</li>
            <li>Recognition for safety achievements</li>
          </ul>

          <div class="highlight-box">
            <h4>Sample Policy Statement</h4>
            <p><em>"[Company Name] is committed to providing a safe and healthful workplace for all employees. Safety is everyone's responsibility. All employees are expected to follow safe work practices, report hazards, and participate in our safety program."</em></p>
          </div>

          <h3>Key Program Components</h3>
          <ul>
            <li><strong>Safety committee</strong> - Worker-management collaboration</li>
            <li><strong>Hazard reporting system</strong> - Easy way to report concerns</li>
            <li><strong>Training program</strong> - New hire and ongoing training</li>
            <li><strong>Inspection program</strong> - Regular workplace inspections</li>
            <li><strong>Emergency procedures</strong> - Evacuation, first aid, reporting</li>
            <li><strong>Return-to-work program</strong> - Support for injured workers</li>
          </ul>
        `,
        keyPoints: [
          "Management commitment is essential for safety success",
          "Involve workers at all levels in safety",
          "Document your policies in writing",
          "Review and update policies regularly"
        ]
      },
      {
        id: 5,
        title: "Continuous Improvement",
        duration: "45 min",
        content: `
          <h3>The Safety Improvement Cycle</h3>
          <p>Safety is not a destination - it's a continuous journey of improvement.</p>

          <div class="highlight-box">
            <h4>Plan-Do-Check-Act (PDCA)</h4>
            <ol>
              <li><strong>Plan</strong> - Identify hazards, set goals, develop action plans</li>
              <li><strong>Do</strong> - Implement controls and training</li>
              <li><strong>Check</strong> - Monitor results, measure performance</li>
              <li><strong>Act</strong> - Review, learn, make improvements</li>
            </ol>
          </div>

          <h3>Leading vs. Lagging Indicators</h3>
          <table>
            <tr>
              <th>Lagging Indicators</th>
              <th>Leading Indicators</th>
            </tr>
            <tr>
              <td>Injury rates (after the fact)</td>
              <td>Training completion (preventive)</td>
            </tr>
            <tr>
              <td>Lost workdays</td>
              <td>Hazards reported and corrected</td>
            </tr>
            <tr>
              <td>Workers' comp costs</td>
              <td>Safety inspections completed</td>
            </tr>
            <tr>
              <td>OSHA citations</td>
              <td>Safety meeting attendance</td>
            </tr>
          </table>

          <h3>Building a Safety Culture</h3>
          <p>Signs of a strong safety culture:</p>
          <ul>
            <li>Workers feel comfortable reporting hazards</li>
            <li>Near-miss reporting is encouraged</li>
            <li>Safety is discussed at all levels</li>
            <li>Leaders model safe behavior</li>
            <li>Safety suggestions are acted upon</li>
            <li>Everyone takes personal responsibility</li>
          </ul>

          <div class="highlight-box important">
            <h4>Your Role in Safety</h4>
            <p>Every worker has the right and responsibility to:</p>
            <ul>
              <li>Know the hazards in their work</li>
              <li>Follow safe work practices</li>
              <li>Report hazards and concerns</li>
              <li>Participate in training</li>
              <li>Look out for coworkers</li>
              <li>Stop unsafe work</li>
            </ul>
          </div>
        `,
        keyPoints: [
          "Safety is a continuous improvement process",
          "Track leading indicators, not just lagging indicators",
          "Every worker has a role in workplace safety",
          "A strong safety culture requires everyone's participation"
        ]
      },
      {
        id: 6,
        title: "Final Review & Certification",
        duration: "40 min",
        content: `
          <h3>Congratulations!</h3>
          <p>You've completed all eight modules of the OSHA Safety Training course. Let's review what you've learned.</p>

          <h3>Course Summary</h3>
          <table>
            <tr>
              <th>Module</th>
              <th>Key Takeaways</th>
            </tr>
            <tr>
              <td>1. Introduction to OSHA</td>
              <td>OSHA mission, worker rights, employer responsibilities</td>
            </tr>
            <tr>
              <td>2. Hazard Recognition</td>
              <td>Types of hazards, JHA, Hierarchy of Controls</td>
            </tr>
            <tr>
              <td>3. PPE</td>
              <td>Selection, use, and maintenance of protective equipment</td>
            </tr>
            <tr>
              <td>4. Fire Safety</td>
              <td>Fire prevention, extinguisher use, emergency planning</td>
            </tr>
            <tr>
              <td>5. Electrical Safety</td>
              <td>LOTO, GFCIs, arc flash protection</td>
            </tr>
            <tr>
              <td>6. Ergonomics</td>
              <td>Lifting techniques, workstation setup, MSD prevention</td>
            </tr>
            <tr>
              <td>7. Specialized Topics</td>
              <td>Fall protection, confined spaces, machine guarding</td>
            </tr>
            <tr>
              <td>8. Compliance</td>
              <td>Recordkeeping, investigation, continuous improvement</td>
            </tr>
          </table>

          <div class="highlight-box important">
            <h4>Your Certificate</h4>
            <p>Upon passing the final assessment with a score of 80% or higher, you will receive a verifiable Certificate of Completion. This certificate demonstrates your understanding of OSHA safety fundamentals.</p>
          </div>

          <h3>Next Steps</h3>
          <ol>
            <li>Complete the final assessment (80% required to pass)</li>
            <li>Download your certificate</li>
            <li>Apply what you've learned in your workplace</li>
            <li>Share your knowledge with coworkers</li>
            <li>Continue learning - safety is a lifelong journey</li>
          </ol>

          <h3>Stay Safe!</h3>
          <p>Remember: Safety isn't just about following rules - it's about going home healthy at the end of every day. The knowledge you've gained can protect you, your coworkers, and your family.</p>

          <p><strong>Thank you for your commitment to workplace safety!</strong></p>
        `,
        keyPoints: [
          "You've completed all 8 modules of OSHA training",
          "Pass the final assessment with 80% or higher for certification",
          "Apply your knowledge in the workplace every day",
          "Safety is everyone's responsibility"
        ]
      }
    ],
    summary: "Compliance and continuous improvement ensure lasting safety success. You're now ready for your final certification!",
    nextSteps: ["Review OSHA recordkeeping requirements", "Prepare for final exam", "Complete Module 8 quiz and certification"]
  }
};

export default moduleContent;
